import jwtDecode from 'jwt-decode';
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';


import config from '../../config';

// content type
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        let message;

        if (error && error.response && error.response.status === 404) {
            // window.location.href = '/not-found';
        } else if (error && error.response && error.response.status === 403) {
            // A 403 almost always means the active workspace is no longer valid for this
            // session (e.g. after switching accounts or logging back in). Drop the stale
            // workspace and send the user to the workspace selector instead of hard-looping
            // on a non-existent /access-denied route.
            try {
                sessionStorage.removeItem('active_workspace');
                localStorage.removeItem('active_workspace');
                delete axios.defaults.headers.common['X-Workspace-ID'];
            } catch (e) {
                /* ignore */
            }
            const path = window.location.pathname || '';
            const onSafePage =
                path.indexOf('/workspace/select') !== -1 ||
                path.indexOf('/workspace/create') !== -1 ||
                path.indexOf('/auth/') !== -1;
            if (!onSafePage) {
                window.location.href = '/workspace/select';
            }
        } else if (error && error.response && error.response.status === 402) {
            // Redirect to pricing disabled
            // if (window.location.pathname !== '/pages/pricing') {
            //     window.location.href = '/pages/pricing';
            // }
        } else {
            switch (error?.response?.status) {
                default: {
                    message =
                        error.response && error.response.data ? error.response.data['message'] : error.message || error;
                }
            }
            return Promise.reject(message);
        }
        return Promise.reject(error);
    }
);

const AUTH_SESSION_KEY = 'shreyu_user';

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
    if (token) axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    else delete axios.defaults.headers.common['Authorization'];
};

/**
 * Sets the default workspace ID header
 * @param {*} workspaceId
 */
const setWorkspaceIdHeader = (workspaceId: string | number | null) => {
    if (workspaceId) axios.defaults.headers.common['X-Workspace-ID'] = String(workspaceId);
    else delete axios.defaults.headers.common['X-Workspace-ID'];
};

/**
 * Reads the active workspace id from storage (same source that seeds the
 * X-Workspace-ID header). Use it to send workspace_id explicitly on requests
 * that must be workspace-scoped (e.g. multipart uploads where a redirect could
 * drop the header), since the API accepts workspace_id as a request value too.
 */
const getActiveWorkspaceId = (): number | string | null => {
    try {
        const stored = sessionStorage.getItem('active_workspace') || localStorage.getItem('active_workspace');
        if (!stored) return null;
        const ws = JSON.parse(stored);
        return ws?.id ?? null;
    } catch (e) {
        return null;
    }
};

const getUserFromCookie = () => {
    let user = sessionStorage.getItem(AUTH_SESSION_KEY);

    // If not found in sessionStorage, get from localStorage
    if (!user) {
        user = localStorage.getItem(AUTH_SESSION_KEY);
    }

    // Parse only if it's a string
    try {
        return user ? (typeof user === "object" ? user : JSON.parse(user)) : null;
    } catch (e) {
        console.error("Error parsing user:", e);
        return null;
    }
};

class APICore {
    /**
     * Fetches data from given url
     */
    // get = (url: string, params: any) => {
    //     let response;
    //     if (params) {
    //         var queryString = params
    //             ? Object.keys(params)
    //                   .map((key) => key + '=' + params[key])
    //                   .join('&')
    //             : '';
    //         response = axios.get(`${url}?${queryString}`, params);
    //     } else {
    //         response = axios.get(`${url}`, params);
    //     }
    //     return response;
    // };
    get = async <T = any>(url: string, params?: Record<string, any>): Promise<AxiosResponse<T>> => {
        return axios.get<T>(url, {
            params: params
        });
    };

    show = async (url: string) => {
        try {
            const response = await axios.get(url);
            return response;
        } catch (error) {
            throw error;
        }
    };

    getFile = (url: string, params: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
            response = axios.get(`${url}?${queryString}`, { responseType: 'blob' });
        } else {
            response = axios.get(`${url}`, { responseType: 'blob' });
        }
        return response;
    };

    getMultiple = (urls: string, params: any) => {
        const reqs = [];
        let queryString = '';
        if (params) {
            queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
        }

        for (const url of urls) {
            reqs.push(axios.get(`${url}?${queryString}`));
        }
        return axios.all(reqs);
    };

    /**
     * post given data to url
     */
    create = async (url: string, data: any) => {
        try {
            const response = await axios.post(url, data);
            return response;
        } catch (error) {
            throw error;
        }
    };



    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(url, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any) => {
        return axios.put(url, data);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(url);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post(url, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.patch(url, formData, config);
    };

    isUserAuthenticated = () => {
        const user = this.getLoggedInUser();

        if (!user) {
            return false;
        }
        const decoded: any = jwtDecode(user.token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
            sessionStorage.removeItem(AUTH_SESSION_KEY);
            localStorage.removeItem(AUTH_SESSION_KEY);

            console.warn('access token expired');
            return false;
        } else {
            return true;
        }
    };

    setLoggedInUser = (session: any) => {
        if (session) {
            localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
            sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        } else {
            clearAllAuthData();
        }
    };
    /**
     * Returns the logged in user
     */
    getLoggedInUser = () => {
        return getUserFromCookie();
    };

    setUserInSession = (modifiedUser: any) => {
        let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
        if (userInfo) {
            const { token, user } = JSON.parse(userInfo);
            this.setLoggedInUser({ token, ...user, ...modifiedUser });
        }
    };
}

/**
 * Completely clears all auth tokens, sessions, local storage, and client cookies
 */
const clearAllAuthData = () => {
    try {
        setAuthorization(null);
        setWorkspaceIdHeader(null);
    } catch (e) {
        console.error('Error clearing auth headers:', e);
    }

    try {
        sessionStorage.clear();
    } catch (e) {
        console.error('Error clearing sessionStorage:', e);
    }

    try {
        // Wipe everything so no stale auth/workspace data can survive a logout and
        // cause the next login to land on a workspace the new session can't access
        // (which previously triggered a 403 -> redirect loop on workspace select).
        // Only the UI theme preference is preserved across logout.
        let layoutSettings: string | null = null;
        try {
            layoutSettings = localStorage.getItem('layout_settings');
        } catch (e) {
            layoutSettings = null;
        }

        localStorage.clear();

        if (layoutSettings) {
            try {
                localStorage.setItem('layout_settings', layoutSettings);
            } catch (e) {
                // ignore – theme will just reset to default
            }
        }
    } catch (e) {
        console.error('Error clearing localStorage:', e);
    }

    try {
        if (typeof document !== 'undefined' && document.cookie) {
            const cookies = document.cookie.split(';');
            const hostname = window.location.hostname;
            const path = '/';
            cookies.forEach((cookie) => {
                const eqPos = cookie.indexOf('=');
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                if (name) {
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${hostname};`;
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=.${hostname};`;
                }
            });
        }
    } catch (e) {
        console.error('Error clearing cookies:', e);
    }
};

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
    const { token } = user;
    if (token) {
        setAuthorization(token);
    }
}

// Check if active workspace is available in sessionStorage or localStorage
const storedWorkspace = sessionStorage.getItem('active_workspace') || localStorage.getItem('active_workspace');
if (storedWorkspace) {
    try {
        const ws = JSON.parse(storedWorkspace);
        if (ws && ws.id) {
            setWorkspaceIdHeader(ws.id);
        }
    } catch (e) {
        console.error("Error parsing stored workspace:", e);
    }
}

// Attach the active workspace to every request so the backend always receives
// X-Workspace-ID, even if the default header was cleared or the workspace changed
// after this module loaded (e.g. switching workspace in another tab).
axios.interceptors.request.use(
    (reqConfig) => {
        const workspaceId = getActiveWorkspaceId();
        if (workspaceId) {
            reqConfig.headers = reqConfig.headers || {};
            reqConfig.headers['X-Workspace-ID'] = String(workspaceId);
        }
        return reqConfig;
    },
    (error) => Promise.reject(error)
);

export { APICore, setAuthorization, setWorkspaceIdHeader, getActiveWorkspaceId, clearAllAuthData };
